import { useState } from 'react'

import {
  CustomFieldType,
  CustomFieldValidators,
} from '@tribeplatform/gql-client/types'
import { MemberPicker } from '@tribeplatform/react-components/EntityPicker'
import { Form } from '@tribeplatform/react-components/Form'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { CustomFieldSubtype, getFieldSetting } from './utils'

export const FieldInput = ({
  field,
  name = undefined,
  onChange = undefined,
  value = undefined,
  ...rest
}) => {
  const [selectedMember, setSelectedMember] = useState(null)

  if (!field) {
    return null
  }
  name = name || field.accessor || field.key
  const subtype = getFieldSetting(field, 'subtype')

  if (field.type === 'member') {
    return (
      <MemberPicker
        value={selectedMember}
        onChange={values => {
          setSelectedMember(values)
          if (typeof onChange === 'function') onChange(values?.id)
        }}
      />
    )
  }

  if (field.type === CustomFieldType.date) {
    return (
      <Form.Input
        name={name}
        onChange={e => {
          if (typeof onChange === 'function') onChange(e.target.value)
        }}
        type="date"
        className="py-1.5"
        {...rest}
      />
    )
  }

  if (field.type === CustomFieldType.boolean) {
    if (rest?.label) {
      if (subtype === CustomFieldSubtype.CHECKBOX) {
        return (
          <Form.Checkbox
            name={name}
            onChange={value => {
              if (typeof onChange === 'function') onChange(value)
            }}
            {...rest}
          />
        )
      }
      return (
        <Form.Toggle
          name={name}
          onChange={value => {
            if (typeof onChange === 'function') onChange(value)
          }}
          {...rest}
        />
      )
    }

    const items = [
      { text: 'Checked', value: true },
      { text: 'Unchecked', value: false },
    ]
    return (
      <Dropdown>
        <Dropdown.Button>
          {items?.find(item => item.value === value)?.text}
        </Dropdown.Button>
        <Dropdown.Items>
          {items.map(item => (
            <Dropdown.Item
              key={name}
              onClick={() => {
                if (typeof onChange === 'function') onChange(item.value)
              }}
            >
              {item.text}
            </Dropdown.Item>
          ))}
        </Dropdown.Items>
      </Dropdown>
    )
  }

  if (field.type === CustomFieldType.array) {
    if (field?.items?.validators?.length) {
      const enumValidator = field?.items?.validators?.find(
        validator => validator.validation === CustomFieldValidators.enum,
      )

      if (enumValidator?.value) {
        try {
          const validValues = JSON.parse(enumValidator.value) as string[]
          if (rest?.label) {
            return (
              <Form.Multiselect
                name={name}
                onChange={value => {
                  if (typeof onChange === 'function') onChange([value])
                }}
                options={validValues}
                {...rest}
              />
            )
          }

          return (
            <Dropdown>
              <Dropdown.Button>{value}</Dropdown.Button>
              <Dropdown.Items>
                {validValues.map(value => (
                  <Dropdown.Item
                    key={value}
                    onClick={() => {
                      if (typeof onChange === 'function') onChange(value)
                    }}
                  >
                    {value}
                  </Dropdown.Item>
                ))}
              </Dropdown.Items>
            </Dropdown>
          )
        } catch (e) {
          logger.error('Could not parse enum array custom field validator')
        }
      }
    }
    return null
  }

  if (field.type === CustomFieldType.richText) {
    return (
      <div>
        <Form.SimpleHtmlEditor name={name} {...rest} />
      </div>
    )
  }

  if (field.type === CustomFieldType.text) {
    if (subtype === CustomFieldSubtype.TEXTAREA && rest?.label) {
      return (
        <Form.Textarea
          name={name}
          onChange={e => {
            if (typeof onChange === 'function') onChange(e.target.value)
          }}
          className="py-1.5"
          {...rest}
        />
      )
    }

    if (field?.validators?.length) {
      const enumValidator = field?.validators?.find(
        validator => validator.validation === CustomFieldValidators.enum,
      )

      if (enumValidator?.value) {
        try {
          const validValues = JSON.parse(enumValidator.value)

          if (rest?.label) {
            return (
              <Form.Select
                name={name}
                items={validValues?.map(value => ({
                  text: value || '-',
                  value,
                }))}
                onChange={value => {
                  if (typeof onChange === 'function') onChange(value)
                }}
                {...rest}
              />
            )
          }

          return (
            <Dropdown>
              <Dropdown.Button>{value}</Dropdown.Button>
              <Dropdown.Items>
                {validValues.map(value => (
                  <Dropdown.Item
                    key={name}
                    onClick={() => {
                      if (typeof onChange === 'function') onChange(value)
                    }}
                  >
                    {value}
                  </Dropdown.Item>
                ))}
              </Dropdown.Items>
            </Dropdown>
          )
        } catch (e) {
          logger.error('Could not parse enum custom field validator')
        }
      }
    }
  }

  let inputType
  if (subtype === CustomFieldSubtype.URL) inputType = 'url'
  if (subtype === CustomFieldSubtype.EMAIL) inputType = 'email'
  if (field?.type === CustomFieldType.number) inputType = 'number'

  return (
    <Form.Input
      name={name}
      type={inputType || 'text'}
      value={value}
      onChange={e => {
        if (typeof onChange === 'function') onChange(e.target.value)
      }}
      className="py-1.5"
      {...rest}
    />
  )
}
